<template>
  <div class="bg-white rounded-xl border border-gray-200/60 card-shadow overflow-hidden">
    <!-- Card header — title only; the tab nav lives on the right
         half so it doesn't compete visually with the map. -->
    <div class="px-6 py-3.5 border-b border-gray-100 flex items-start justify-between gap-3 flex-wrap">
      <div class="min-w-0">
        <h2 class="text-[15px] font-semibold text-gray-900">Geographic Distribution</h2>
        <p class="text-sm text-gray-400 mt-0.5">Listener locations and the top regions worldwide</p>
      </div>
      <span v-if="!loading && locationCount" class="text-sm font-data font-medium text-[var(--color-primary)] bg-[var(--color-primary-light)] px-3 py-1 rounded">
        {{ formatNumber(locationCount) }} locations
      </span>
    </div>

    <!-- Two-pane body when `showMap` is on: map on the left (uses
         session-sourced lat/lng — same data source as the existing
         Lives detail map), top-10 table on the right with three tabs
         (Countries / Regions / Cities). The split collapses to
         stacked on narrow viewports so neither pane gets crushed.
         When `showMap` is off the map pane collapses and the table
         takes the full card width — used to hide the slow
         session-scan query while the KPI/rollup panels are being
         verified. -->
    <div :class="[
      showMap
        ? 'grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-gray-100'
        : ''
    ]">
      <!-- Left: map -->
      <div v-if="showMap" class="p-6">
        <LoadingBlock v-if="loading" height="400" label="Loading map..." />
        <div v-else-if="hasMapData" ref="map" class="h-[500px] rounded-lg overflow-hidden animate-fade-in"></div>
        <div v-else class="h-[500px] flex flex-col items-center justify-center text-center">
          <div class="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
            <svg class="w-5 h-5 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
            </svg>
          </div>
          <p class="text-sm text-gray-500">No geographic data available</p>
        </div>
      </div>

      <!-- Right: tabbed top-10 table -->
      <!-- min-height matches the map pane (500 px map + 48 px
           padding) so switching to a tab with fewer than 10 rows
           doesn't shrink the card. The flex-col pushes the empty
           state / table content to the top and lets blank space
           fill the bottom. -->
      <div class="flex flex-col min-h-[548px]">
        <div class="px-6 py-3.5 border-b border-gray-100 flex items-center justify-between gap-3">
          <p class="text-[13px] font-medium text-gray-500">Top 10 by sessions</p>
          <div class="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            <button
              v-for="t in tabs"
              :key="t.key"
              @click="active = t.key"
              :class="[
                'h-[26px] px-2.5 rounded-md text-[12px] font-medium transition-all duration-150 cursor-pointer',
                active === t.key
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              ]"
            >{{ t.label }}</button>
          </div>
        </div>

        <table class="w-full">
          <thead>
            <tr class="bg-gray-50 border-b border-gray-100">
              <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 w-[48px]">#</th>
              <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">{{ titleHeader }}</th>
              <th class="text-right text-sm font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                <span class="inline-flex items-center gap-1 justify-end align-middle">
                  <span>Sessions</span>
                  <ColumnInfo
                    col-key="sessions"
                    :active="activeHeader === 'sessions'"
                    @toggle="toggleHeader"
                    info="Sessions in the window for this row. A session opens on the first play intent and closes on SDK destroy or a 30-minute continuous-pause timeout."
                  />
                </span>
              </th>
              <th class="text-right text-sm font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 hidden sm:table-cell">
                <span class="inline-flex items-center gap-1 justify-end align-middle">
                  <span>Session Duration</span>
                  <ColumnInfo
                    col-key="sessionDuration"
                    :active="activeHeader === 'sessionDuration'"
                    @toggle="toggleHeader"
                    info="Total wall-clock across sessions for this row — playing time (content + ads) plus paused time."
                  />
                </span>
              </th>
              <th class="text-right text-sm font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 hidden xl:table-cell">
                <span class="inline-flex items-center gap-1 justify-end align-middle">
                  <span>Avg. Session Duration</span>
                  <ColumnInfo
                    col-key="avgSessionDuration"
                    :active="activeHeader === 'avgSessionDuration'"
                    @toggle="toggleHeader"
                    info="Session Duration ÷ Sessions for this row."
                  />
                </span>
              </th>
              <th class="text-right text-sm font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 hidden md:table-cell">
                <span class="inline-flex items-center gap-1 justify-end align-middle">
                  <span>Watch Time</span>
                  <ColumnInfo
                    col-key="watchTime"
                    :active="activeHeader === 'watchTime'"
                    @toggle="toggleHeader"
                    info="Total time the media element was playing content across sessions for this row. Excludes ads (tracked separately) and paused / buffering periods."
                  />
                </span>
              </th>
              <th class="text-right text-sm font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 hidden xl:table-cell">
                <span class="inline-flex items-center gap-1 justify-end align-middle">
                  <span>Avg. Watch Time</span>
                  <ColumnInfo
                    col-key="avgWatchTime"
                    :active="activeHeader === 'avgWatchTime'"
                    @toggle="toggleHeader"
                    info="Watch Time ÷ Sessions for this row."
                  />
                </span>
              </th>
            </tr>
          </thead>

          <tbody v-if="loading">
            <tr v-for="i in 5" :key="'skel-' + i" class="border-b border-gray-50 last:border-b-0">
              <td class="px-4 py-3"><div class="h-3.5 bg-gray-100 rounded skeleton-pulse w-6" /></td>
              <td class="px-4 py-3"><div class="h-3.5 bg-gray-100 rounded skeleton-pulse" :style="{ width: (40 + i * 5) + '%' }" /></td>
              <td class="px-4 py-3"><div class="h-3.5 bg-gray-100 rounded skeleton-pulse w-16 ml-auto" /></td>
              <td class="px-4 py-3 hidden sm:table-cell"><div class="h-3.5 bg-gray-100 rounded skeleton-pulse w-16 ml-auto" /></td>
              <td class="px-4 py-3 hidden xl:table-cell"><div class="h-3.5 bg-gray-100 rounded skeleton-pulse w-14 ml-auto" /></td>
              <td class="px-4 py-3 hidden md:table-cell"><div class="h-3.5 bg-gray-100 rounded skeleton-pulse w-16 ml-auto" /></td>
              <td class="px-4 py-3 hidden xl:table-cell"><div class="h-3.5 bg-gray-100 rounded skeleton-pulse w-14 ml-auto" /></td>
            </tr>
          </tbody>

          <tbody v-else-if="rows.length" class="divide-y divide-gray-50">
            <tr
              v-for="(row, i) in rows"
              :key="row.id"
              class="hover:bg-gray-50/50 transition-colors"
            >
              <td class="px-4 py-3">
                <span class="text-sm font-data text-gray-400">{{ i + 1 }}</span>
              </td>
              <td class="px-4 py-3 max-w-[180px]">
                <span class="text-[14px] text-gray-900 truncate block">{{ row.title || 'Unknown' }}</span>
              </td>
              <td class="px-4 py-3 text-right">
                <span class="text-sm font-data text-gray-900">{{ formatNumber(row.count) }}</span>
              </td>
              <td class="px-4 py-3 text-right hidden sm:table-cell">
                <span class="text-sm font-data text-gray-500">{{ formatDurationSeconds(row.totalSessionDuration) }}</span>
              </td>
              <td class="px-4 py-3 text-right hidden xl:table-cell">
                <span class="text-sm font-data text-gray-500">{{ formatDurationSeconds(row.avgSessionDuration) }}</span>
              </td>
              <td class="px-4 py-3 text-right hidden md:table-cell">
                <span class="text-sm font-data text-gray-500">{{ formatDurationSeconds(row.totalTime) }}</span>
              </td>
              <td class="px-4 py-3 text-right hidden xl:table-cell">
                <span class="text-sm font-data text-gray-500">{{ formatDurationSeconds(row.avgTime) }}</span>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-if="!loading && !rows.length" class="px-6 py-12 text-center flex-1 flex flex-col items-center justify-center">
          <div class="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
            <svg class="w-5 h-5 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
            </svg>
          </div>
          <p class="text-sm text-gray-500">{{ emptyLabel }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import LoadingBlock from '../../../components/LoadingBlock.vue'
import ColumnInfo from './ColumnInfo.vue'

/**
 * Geographic Distribution card. Two panes:
 *   - Left  : world map with circle markers (lat/lng + session
 *             count, sourced from `geography` — same shape and
 *             source as the Lives detail map).
 *   - Right : tabbed top-10 table (Countries / Regions / Cities)
 *             ranked by validated streams. Data comes from
 *             `topGeo.{country,region,city}` produced server-side
 *             from the per-dimension rollup maps.
 *
 * Map and table read from different shapes deliberately: the map
 * needs lat/lng (only present on session-sourced rows), the table
 * needs Streams/Time (only present on rollup-sourced rows). One
 * pane per data source.
 */
export default {
  name: 'GeoDistributionCard',
  components: { LoadingBlock, ColumnInfo },
  props: {
    loading: { type: Boolean, default: false },
    // `topGeo` from the API: { country, region, city, location }.
    // Each is an array of `{ id, title, streams, playbackStarts,
    // totalTime, avgTime }`. The `location` slot is unused in this
    // layout — the map covers per-location distribution.
    data: { type: Object, default: () => ({}) },
    // Session-sourced geography for the map: `[{ _id: { country,
    // city }, count, lat, lng }, ...]`. Same shape the Lives detail
    // page uses — passed in from the parent so this component
    // doesn't have to know the response root.
    geography: { type: Array, default: () => [] },
    // Hide the map pane entirely and let the top-10 table span the
    // full card width. Used to skip the ~3s geography-map session
    // scan while other panels are being verified.
    showMap: { type: Boolean, default: true }
  },
  data() {
    return {
      active: 'country',
      tabs: [
        { key: 'country', label: 'Countries', titleHeader: 'Country' },
        { key: 'region',  label: 'Regions',   titleHeader: 'Region'  },
        { key: 'city',    label: 'Cities',    titleHeader: 'City'    }
      ],
      map: null,
      activeHeader: null
    }
  },
  computed: {
    rows() {
      const list = this.data?.[this.active]
      return Array.isArray(list) ? list : []
    },
    titleHeader() {
      return this.tabs.find(t => t.key === this.active)?.titleHeader || 'Title'
    },
    emptyLabel() {
      const labels = {
        country: 'No country data for this period',
        region:  'No region data for this period',
        city:    'No city data for this period'
      }
      return labels[this.active] || 'No data'
    },
    hasMapData() {
      return Array.isArray(this.geography) && this.geography.some(g => g.lat && g.lng)
    },
    locationCount() {
      return Array.isArray(this.geography) ? this.geography.length : 0
    }
  },
  watch: {
    geography: { handler() { this.$nextTick(() => this.renderMap()) }, deep: true },
    loading(v) { if (!v) this.$nextTick(() => this.renderMap()) }
  },
  mounted() {
    this.$nextTick(() => this.renderMap())
    this._onDocClick = () => { this.activeHeader = null }
    document.addEventListener('click', this._onDocClick)
  },
  beforeUnmount() {
    if (this.map) { this.map.remove(); this.map = null }
    if (this._onDocClick) document.removeEventListener('click', this._onDocClick)
  },
  methods: {
    toggleHeader(key) {
      this.activeHeader = this.activeHeader === key ? null : key
    },
    renderMap() {
      if (this.loading || !this.hasMapData) return
      const container = this.$refs.map
      if (!container) return
      if (this.map) this.map.remove()
      // Disable animations — they fight against the parent
      // template's v-if remounting on data swap and occasionally
      // leave half-faded tiles when the user clicks Search rapidly.
      this.map = L.map(container, { zoomAnimation: false, fadeAnimation: false }).setView([20, 0], 2)
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap'
      }).addTo(this.map)
      for (const geo of this.geography) {
        if (!geo.lat || !geo.lng) continue
        L.circleMarker([geo.lat, geo.lng], {
          radius: Math.min(5 + (geo.count / 10), 20),
          fillColor: '#3393ff',
          color: '#fff',
          weight: 2,
          opacity: 1,
          fillOpacity: 0.6
        }).addTo(this.map).bindPopup(
          `<strong>${this.locationLabel(geo._id)}</strong><br>Sessions: ${geo.count}`
        )
      }
    },
    locationLabel(id) {
      if (id?.city && id?.country) return `${id.city}, ${id.country}`
      return id?.country || 'Unknown'
    },
    formatDurationSeconds(s) {
      if (!s) return '0s'
      const sec = Math.floor(s)
      const m = Math.floor(sec / 60)
      const h = Math.floor(m / 60)
      if (h > 0) return `${h}h ${m % 60}m`
      if (m > 0) return `${m}m ${sec % 60}s`
      return `${sec}s`
    },
    formatNumber(n) {
      return (n || 0).toLocaleString()
    }
  }
}
</script>
